import * as Sentry from '@sentry/react';
import { useEffect, useState, type ReactElement } from 'react';
import { RouterProvider } from 'react-router-dom';

import { SentryErrorFallback } from '@/components/error/sentry-error-fallback';
import { runAuthBootstrap } from '@/app/auth-bootstrap';
import { systemConfig } from '@/config/system.config';
import { useAuthStore } from '@/store/auth.store';
import { useAppConfigStore } from '@/store/app-config.store';

import { router } from './router';

function AuthBootstrapFallback(): ReactElement {
  return (
    <main className="flex min-h-screen items-center justify-center bg-muted/50 p-4">
      <p className="text-muted-foreground text-sm" role="status" aria-live="polite">
        Checking your session...
      </p>
    </main>
  );
}

export default function App(): ReactElement {
  const [authBootstrapReady, setAuthBootstrapReady] = useState(false);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const loadAppConfig = useAppConfigStore((state) => state.loadConfig);

  useEffect(() => {
    let cancelled = false;
    void runAuthBootstrap().then(() => {
      if (!cancelled) {
        setAuthBootstrapReady(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !systemConfig.tenantId) {
      return;
    }
    void loadAppConfig(systemConfig.tenantId);
  }, [isAuthenticated, loadAppConfig]);

  return (
    <Sentry.ErrorBoundary
      fallback={<SentryErrorFallback />}
      showDialog={false}
      onError={(error, componentStack) => {
        try {
          Sentry.captureException(error, { extra: { componentStack } });
        } catch {
          // Sentry may not be initialized yet
        }
        console.error('Root error boundary caught:', error, componentStack);
      }}
    >
      {authBootstrapReady ? <RouterProvider router={router} /> : <AuthBootstrapFallback />}
    </Sentry.ErrorBoundary>
  );
}
